/**
 * reportExport.ts — Enterprise report export utilities.
 * Excel: SheetJS (xlsx). Print: window.print() with print-ready DOM.
 */
import * as XLSX from 'xlsx';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ExcelColumn {
  header: string;
  key: string;
  width?: number;
  numFmt?: string;   // e.g. '#,##0.00' for amounts
  align?: 'left' | 'right' | 'center';
}

export interface ExcelSheetConfig {
  sheetName: string;
  columns: ExcelColumn[];
  rows: Record<string, unknown>[];
  title?: string;        // Report title printed at top
  subtitle?: string;     // Company name / date range
  totalsRow?: Record<string, unknown>;  // Optional bold totals row
}

// ── Excel Export ──────────────────────────────────────────────────────────────

export function exportToExcel(config: ExcelSheetConfig, fileName: string): void {
  const wb = XLSX.utils.book_new();

  // Build array-of-arrays for the sheet
  const aoa: unknown[][] = [];

  // Title rows
  if (config.title) {
    aoa.push([config.title]);
    aoa.push([config.subtitle ?? '']);
    aoa.push([]); // blank separator
  }

  // Header row
  aoa.push(config.columns.map(c => c.header));

  // Data rows
  for (const row of config.rows) {
    aoa.push(config.columns.map(c => row[c.key] ?? ''));
  }

  // Totals row
  if (config.totalsRow) {
    aoa.push(config.columns.map(c => config.totalsRow![c.key] ?? ''));
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths
  ws['!cols'] = config.columns.map(c => ({ wch: c.width ?? 18 }));

  // Merge title across all columns if title exists
  if (config.title) {
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: config.columns.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: config.columns.length - 1 } },
    ];
  }

  XLSX.utils.book_append_sheet(wb, ws, config.sheetName.slice(0, 31));
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}

// ── CSV Export ────────────────────────────────────────────────────────────────

/**
 * RFC-4180-safe CSV export: fields containing a comma, double quote, or newline
 * are quoted (embedded quotes doubled). A UTF-8 BOM prefix keeps Excel happy.
 * Note: callers are responsible for audit logging (pages log via toolbar).
 */
export function exportToCsvRows(
  columns: ExcelColumn[],
  rows: Record<string, unknown>[],
  filename: string,
): void {
  const escape = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const lines: string[] = [
    columns.map(c => escape(c.header)).join(','),
    ...rows.map(row => columns.map(c => escape(row[c.key])).join(',')),
  ];

  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.toLowerCase().endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Print ─────────────────────────────────────────────────────────────────────

/**
 * Triggers browser print dialog for a specific element.
 * The element should have a `data-print-section` attribute.
 * CSS `@media print` hides everything else and shows only this element.
 *
 * Usage: Call `printElement(document.getElementById('report-print-area'))`.
 */
export function printReport(title: string): void {
  // Set document title for print header
  const originalTitle = document.title;
  document.title = title;
  window.print();
  // Restore after print dialog closes
  setTimeout(() => { document.title = originalTitle; }, 1000);
}
